import create from "zustand";
import { StoreStateType } from "../types/store";
import { devtools } from "zustand/middleware";
import { SizeType } from "../types/size";
import { clientCache, CLIENT_CACHE_KEYS } from "../utils/clientCache";

const useStore = create<StoreStateType>()(
  devtools(
    (set, get): StoreStateType => ({
      setKeepFrame: async (id, keepFrame) => {
        await fetch("/api/images", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            imageId: id,
            keepFrame: keepFrame,
          }),
        });
        return set((state) => ({
          loading: false,
          images: state.images.map((image) => {
            if (image._id === id) {
              image.keepFrame = keepFrame;
            }
            return image;
          }),
        }));
      },
      addImage: (image) => {
        // const newImages = new Array(amount).fill(image);
        set((state) => ({
          images: [...state.images, image],
        }));
      },
      setSentMail: async (organizerId: string, isStandhouder) => {
        if (isStandhouder) {
          // for standhoduers organizerId is the link of the standhouder
          const standhouders = get().standhouders;
          set({
            standhouders: standhouders.map((standhouder) =>
              standhouder.link === organizerId
                ? { ...standhouder, emailSent: true }
                : standhouder
            ),
          });
          await fetch(`/api/standhouders/`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ link: organizerId, emailSent: true }),
          });
        } else {
          const organizers = get().organizers;
          set({
            organizers: organizers.map((organizer) =>
              organizer._id === organizerId
                ? { ...organizer, emailSent: true }
                : organizer
            ),
          });
          await fetch(`/api/publicOrganizers/`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ _id: organizerId, emailSent: true }),
          });
        }
      },
      deleteInfoToStandhouder: async (info, standhouderId) => {
        await fetch("/api/info", {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            standhouderId,
            driveId: info.driveId,
            itemId: info.itemId,
          }),
        });
        set({
          standhouders: get().standhouders.map((x) => {
            if (x.link === standhouderId) {
              return {
                ...x,
                infos: (x.infos || []).filter((y) => y.itemId !== info.itemId),
              };
            }
            return x;
          }),
          loading: false,
        });
      },
      addInfoToStandhouder: (standhouderId, info) => {
        const { standhouders } = get();
        const standhouder = standhouders.find(
          (standhouder) => standhouder.link === standhouderId
        );
        if (standhouder) {
          standhouder.infos = [...(standhouder.infos || []), info];
        }
        set({ standhouders });
      },
      deleteImage: async (image) => {
        await fetch(`/api/images`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: image._id,
            deleteFromDrive: image.preview,
          }),
        });
        return set({
          loading: false,
          images: get().images.filter((x) => x._id !== image._id),
        });
      },
      images: [],
      error: "",
      setError: (error: string) => set((state) => ({ error })),
      suppliers: [],
      addSupplier: async (supplier) => {
        const newSupplier = await fetch("/api/suppliers", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(supplier),
        });
        const newSupplierData = await newSupplier.json();
        set({
          suppliers: [...get().suppliers, newSupplierData],
          loading: false,
        });
      },
      deleteSupplier: async (supplierId) => {
        await fetch(`/api/suppliers`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ supplierId }),
        });
        set((state) => ({
          ...state,
          suppliers: state.suppliers.filter(
            (supplier) => supplier._id !== supplierId
          ),
          loading: false,
        }));
      },
      deleteSize: async (sizeId) => {
        await fetch(`/api/sizes`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ sizeId }),
        });
        set((state) => ({
          ...state,
          sizes: state.sizes.filter((size) => size._id !== sizeId),
          loading: false,
        }));
      },
      clearData: () => {
        return set({ organizers: [], isAdmin: false });
      },
      isAdmin: false,
      loadDataStandhouder: async (standhouderId) => {
        const { dataLoaded } = get();
        if (dataLoaded) return set({ loading: false });

        const response = await fetch(
          "/api/loadStandhouderData/" + standhouderId
        );
        if (response.status === 404) {
          console.log("???? not found");
          set({
            loading: false,
            error: "Deze standhouder is niet gevonden",
            organizers: [],
            standhouders: [],
            sizes: [],
            dataLoaded: true,

            images: [],
            isAdmin: false,
          });

          // redirect to 404
          location.href = "/404";

          return;
        }
        const data = await response.json();

        set({
          organizers: data.organizers,
          standhouders: data.standhouders,
          sizes: data.sizes,
          dataLoaded: true,
          loading: false,
          images: data.images,
          isAdmin: data.isAdmin,
        });
      },
      loadDataOrganizer: async (organizerId) => {
        const { dataLoaded } = get();
        if (dataLoaded) return set({ loading: false });

        // Try client cache first
        const cacheKey = CLIENT_CACHE_KEYS.ORGANIZER_DATA(organizerId);
        const cachedData = clientCache.get<any>(cacheKey);

        if (cachedData) {
          console.log(cachedData)
          console.log(`Using cached organizer data for ${organizerId}`);
          set({
            organizers: cachedData.organizers || [],
            standhouders: cachedData.standhouders || [],
            sizes: cachedData.sizes || [],
            dataLoaded: true,
            loading: false,
            images: cachedData.images || [],
            isAdmin: cachedData.isAdmin || false,
          });

          // Background refresh if needed
          if (clientCache.shouldBackgroundRefresh(cacheKey)) {
            console.log(`Background refresh for organizer ${organizerId}`);
            fetch("/api/loadOrganizerData/" + organizerId)
              .then((response) => response.json())
              .then((data) => {
                clientCache.set(cacheKey, data, 5);
                set((currentState) => ({
                  ...currentState,
                  organizers: data.organizers || [],
                  standhouders: data.standhouders || [],
                  sizes: data.sizes || [],
                  images: data.images || [],
                  isAdmin: data.isAdmin || false,
                }));
              })
              .catch((error) =>
                console.error("Background refresh failed:", error)
              );
          }
          return;
        }

        // No cache, fetch from server
        set({ loading: true });
        try {
          const response = await fetch("/api/loadOrganizerData/" + organizerId);
          const data = await response.json();

          // Cache the data
          clientCache.set(cacheKey, data, 5);

          set({
            organizers: data.organizers,
            standhouders: data.standhouders,
            sizes: data.sizes,
            dataLoaded: true,
            loading: false,
            images: data.images,
            isAdmin: data.isAdmin,
          });
        } catch (error) {
          console.error("Error loading organizer data:", error);
          set({ loading: false, error: "Failed to load organizer data" });
        }
      },
      loadData: async () => {
        const { dataLoaded } = get();
        if (dataLoaded) return set({ loading: false });

        // Try to get data from client cache first
        const cachedAdminData = clientCache.get<any>(
          CLIENT_CACHE_KEYS.ADMIN_DATA
        );
        const cachedSizes = clientCache.get<SizeType[]>(
          CLIENT_CACHE_KEYS.SIZES
        );
        const cachedOrganizers = clientCache.get<any>(
          CLIENT_CACHE_KEYS.ORGANIZERS
        );
        const cachedSuppliers = clientCache.get<any[]>(
          CLIENT_CACHE_KEYS.SUPPLIERS
        );

        // If we have cached data, use it immediately
        if (
          cachedAdminData &&
          cachedSizes &&
          cachedOrganizers &&
          cachedSuppliers
        ) {
          console.log("cachedSizes",cachedSizes)
          console.log("Using cached data for immediate display");
          set({
            sizes: cachedSizes,
            organizers: cachedOrganizers.organizers || [],
            suppliers: cachedSuppliers,
            isAdmin: true, // Assume admin if we have cached data
            dataLoaded: true,
            standhouders: cachedAdminData.standhouders || [],
            images: cachedAdminData.images || [],
            loading: false,
          });

          // Check if any data needs background refresh
          const needsRefresh = [
            clientCache.shouldBackgroundRefresh(CLIENT_CACHE_KEYS.ADMIN_DATA),
            clientCache.shouldBackgroundRefresh(CLIENT_CACHE_KEYS.SIZES),
            clientCache.shouldBackgroundRefresh(CLIENT_CACHE_KEYS.ORGANIZERS),
            clientCache.shouldBackgroundRefresh(CLIENT_CACHE_KEYS.SUPPLIERS),
          ].some(Boolean);

          if (needsRefresh) {
            console.log("Starting background refresh...");
            // Mark as refreshing to prevent duplicate requests
            clientCache.markAsRefreshing(CLIENT_CACHE_KEYS.ADMIN_DATA);
            clientCache.markAsRefreshing(CLIENT_CACHE_KEYS.SIZES);
            clientCache.markAsRefreshing(CLIENT_CACHE_KEYS.ORGANIZERS);
            clientCache.markAsRefreshing(CLIENT_CACHE_KEYS.SUPPLIERS);

            // Fetch fresh data in background
            get().backgroundRefreshAdminData();
          }

          return;
        }

        // No cached data, fetch from server and show loading
        console.log("No cached data, fetching from server...");
        set({ loading: true });

        try {
          const [response, sizesRaw, organizersRaw, suppliersRaw] =
            await Promise.all([
              fetch("/api/loadAdminData"),
              fetch("/api/sizes"),
              fetch("/api/organizers"),
              fetch("/api/suppliers"),
            ]);

          const [data, sizes, organizers, suppliers] = await Promise.all([
            response.json(),
            sizesRaw.json(),
            organizersRaw.json(),
            suppliersRaw.json(),
          ]);

          // Cache the fresh data
          clientCache.set(CLIENT_CACHE_KEYS.ADMIN_DATA, data, 5);
          clientCache.set(CLIENT_CACHE_KEYS.SIZES, sizes, 10); // Sizes change less frequently
          clientCache.set(CLIENT_CACHE_KEYS.ORGANIZERS, organizers, 10);
          clientCache.set(CLIENT_CACHE_KEYS.SUPPLIERS, suppliers, 15); // Suppliers change least frequently

          set({
            sizes: sizes,
            organizers: organizers.organizers,
            suppliers: suppliers,
            isAdmin: response.status === 200,
            dataLoaded: true,
            standhouders: data.standhouders,
            images: data.images,
            loading: false,
          });
        } catch (error) {
          console.error("Error loading data:", error);
          set({ loading: false, error: "Failed to load data" });
        }
      },

      // Background refresh method (doesn't show loading state)
      backgroundRefreshAdminData: async () => {
        try {
          console.log("Background refresh: Fetching fresh data...");
          const [response, sizesRaw, organizersRaw, suppliersRaw] =
            await Promise.all([
              fetch("/api/loadAdminData"),
              fetch("/api/sizes"),
              fetch("/api/organizers"),
              fetch("/api/suppliers"),
            ]);

          const [data, sizes, organizers, suppliers] = await Promise.all([
            response.json(),
            sizesRaw.json(),
            organizersRaw.json(),
            suppliersRaw.json(),
          ]);

          // Update cache with fresh data
          clientCache.set(CLIENT_CACHE_KEYS.ADMIN_DATA, data, 5);
          clientCache.set(CLIENT_CACHE_KEYS.SIZES, sizes, 10);
          clientCache.set(CLIENT_CACHE_KEYS.ORGANIZERS, organizers, 10);
          clientCache.set(CLIENT_CACHE_KEYS.SUPPLIERS, suppliers, 15);

          // Silently update the store with fresh data
          set((currentState) => ({
            ...currentState,
            sizes: sizes,
            organizers: organizers.organizers,
            suppliers: suppliers,
            standhouders: data.standhouders,
            images: data.images,
          }));

          console.log("Background refresh completed");
        } catch (error) {
          console.error("Background refresh failed:", error);
        }
      },
      deleteOrganizer: async (organizerId: string) => {
        await fetch(`/api/organizers/`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            organizerId,
          }),
        });

        set({
          organizers: get().organizers.map((org) =>
            org._id === organizerId ? { ...org, active: false } : org
          ),
          loading: false,
        });
      },
      editOrganizer: async (org) => {
        const editedOrg = await fetch(`/api/organizers`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(org),
        });
        const editedOrgData = await editedOrg.json();
        set({
          organizers: get().organizers.map((organizer) => {
            if (organizer._id === org._id) {
              return editedOrgData;
            }
            return organizer;
          }),
          loading: false,
        });
      },
      editStandhouder: async (standhouder) => {
        const editedOrg = await fetch(`/api/standhouders`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(standhouder),
        });
        const editedOrgData = await editedOrg.json();
        set({
          standhouders: get().standhouders.map((sh) => {
            if (sh._id === standhouder._id) {
              return editedOrgData;
            }
            return sh;
          }),
          loading: false,
        });
      },
      addSize: async (size) => {
        const newSize = await fetch("/api/sizes", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(size),
        });
        const newSizeJson = (await newSize.json()) as SizeType[];
        set({
          sizes: [...get().sizes, ...newSizeJson],
          loading: false,
        });
      },
      editSize: async (size) => {
        await fetch("/api/sizes", {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(size),
        });
        set((state) => ({
          ...state,
          sizes: state.sizes.map((s) => {
            if (s._id === size._id) {
              return size;
            }
            return s;
          }),
          loading: false,
        }));
      },
      dataLoaded: false,

      organizers: [],
      standhouders: [],
      setStandhouders: (standhouders) => {
        return set({
          standhouders,
        });
      },
      sizes: [],
      addStandhouder: async (standhouder) => {
        const addStandhoudersResponse = await fetch("/api/standhouders", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(standhouder),
        });
        const addedStandhoudersJson = await addStandhoudersResponse.json();
        console.log("addedStandhouders", addedStandhoudersJson);
        const addedStandhouders = addedStandhoudersJson.standhouder;

        return set((state) => ({
          ...state,
          standhouders: Array.isArray(addedStandhouders)
            ? [...state.standhouders, ...addedStandhouders]
            : [...state.standhouders, addedStandhouders],
          loading: false,
        }));
      },
      addOrganizer: async (organizer) => {
        const newOrgResponse = await fetch("/api/organizers", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(organizer),
        });
        const newOrg = await newOrgResponse.json();
        set((state) => ({
          ...state,
          organizers: [...state.organizers, newOrg.company],
          loading: false,
        }));
      },
      loading: false,
      setLoading: (loading: boolean) => set((state) => ({ ...state, loading })),
      status: "",
      setStatus: (status) => set({ status }),
    })
  )
);

export default useStore;
